import L from 'leaflet'
import { maplibreGL } from '@maplibre/maplibre-gl-leaflet'
import { setWorkerUrl } from 'maplibre-gl'
// `?worker&url` : Vite empaquette le worker (avec le module partagé qu'il
// importe) et renvoie l'adresse du fichier produit.
import urlWorker from 'maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url'
import 'leaflet/dist/leaflet.css'
import 'maplibre-gl/dist/maplibre-gl.css'
import './fondDeCarte.css'
import { creerControleFond } from './controleFond'
import {
  ATTRIBUTION_OPENFREEMAP,
  FONDS_INTEGRES,
  ZOOM_MAX_CARTE,
  creerMagasinFonds,
  type FondDeCarte,
} from './fondsDeCarte'

/**
 * ⚠️ Le worker de MapLibre DOIT être déclaré à la main.
 *
 * Par défaut, MapLibre cherche `maplibre-gl-worker.mjs` à côté du fichier qui
 * le contient. Une fois le code empaqueté par Vite (en développement comme en
 * production), ce voisin n'existe plus : « Worker failed to load » — et la
 * carte reste VIDE, sans autre symptôme que cette erreur dans la console.
 */
setWorkerUrl(urlWorker)

/**
 * Fond de carte commun aux cartes de la console. Par DÉFAUT : OpenFreeMap, style
 * « liberty » (détaillé : commerces, restaurants, pharmacies, banques, stations).
 * L'utilisateur peut en choisir un autre — ou en ajouter un — par le contrôle
 * « Fond de carte » en haut à droite de chaque carte (voir `controleFond.ts`,
 * `fondsDeCarte.ts` et `SwiftDeliver/docs/FONDS-DE-CARTE.md`).
 *
 * POURQUOI PAS CARTO. Les tuiles `basemaps.cartocdn.com` étaient appelées sans
 * clé : CARTO l'exige et limite l'usage commercial. OpenFreeMap est gratuit,
 * sans clé ni compte, et son usage commercial est autorisé (décision : aucun
 * fond de carte payant).
 *
 * Les fonds vectoriels sont affichés avec MapLibre GL, branché sur Leaflet par
 * `@maplibre/maplibre-gl-leaflet` ; les fonds raster (OpenStreetMap, Relief,
 * fonds ajoutés) par `L.tileLayer`. Leaflet garde les marqueurs, les popups,
 * l'épingle déplaçable et les événements ; seul le FOND change — changer de
 * fond ne retire ni ne recrée aucune autre couche.
 */

/**
 * Préfixe des clés de `localStorage` de CETTE console : le choix de fond et les
 * fonds ajoutés ne se mélangent pas d'une console à l'autre. SEULE ligne qui
 * diffère entre les trois copies de ce fichier.
 */
export const PREFIXE_STOCKAGE = 'se_dispatch'

/** UN magasin par console : toutes les cartes ouvertes (et celles de l'écran suivant) lisent le même choix. */
export const magasinFonds = creerMagasinFonds({ prefixe: PREFIXE_STOCKAGE })

// Autre onglet de la même console : son choix s'applique ici aussi, sans recharger.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === null || e.key.startsWith(`${PREFIXE_STOCKAGE}_fond_carte_`)) magasinFonds.recharger()
  })
}

/** Le fond par défaut (liberty) : conservé pour les anciens appelants. */
export const STYLE_FOND = FONDS_INTEGRES[0]!.url
export const ATTRIBUTION_FOND = ATTRIBUTION_OPENFREEMAP

/**
 * Options Leaflet à passer à `L.map` pour que le fond MapLibre se comporte.
 *
 * MapLibre borne la latitude plus strictement que Leaflet : sans `maxBounds`,
 * un glissement vers les pôles désynchronise le fond et les marqueurs.
 * `minZoom: 1` évite le même défaut au zoom 0. Bamako est loin de ces limites.
 */
export const OPTIONS_CARTE: L.MapOptions = {
  zoomControl: true,
  attributionControl: true,
  minZoom: 1,
  maxZoom: ZOOM_MAX_CARTE,
  maxBounds: [
    [-85, -Infinity],
    [85, Infinity],
  ],
  maxBoundsViscosity: 1,
}

function creerCouche(fond: FondDeCarte): L.Layer {
  if (fond.type === 'vecteur') {
    return maplibreGL({
      style: fond.url,
      // Texte d'attribution fixe : il s'affiche tout de suite, avant même que le
      // style distant soit chargé, et ne dépend pas de ce que celui-ci déclare.
      attributionControl: { customAttribution: fond.attributionHtml },
    })
  }
  return L.tileLayer(fond.url, {
    attribution: fond.attributionHtml,
    // Au-delà du zoom où le serveur a des tuiles, on AGRANDIT la dernière
    // (sinon le fond disparaîtrait alors que la carte permet de zoomer plus).
    maxNativeZoom: fond.zoomMax ?? ZOOM_MAX_CARTE,
    maxZoom: ZOOM_MAX_CARTE,
    subdomains: 'abc',
  })
}

/**
 * Un fond raster dont le serveur ne répond pas (adresse saisie de travers,
 * serveur hors ligne) laisse une carte VIDE sans autre symptôme : on le DIT,
 * en une ligne, tant qu'aucune tuile n'est arrivée.
 */
function surveillerTuiles(carte: L.Map, couche: L.TileLayer, nom: string): void {
  let erreurs = 0
  let recues = 0
  let alerte: HTMLElement | null = null
  const effacer = () => {
    alerte?.remove()
    alerte = null
  }
  couche.on('tileload', () => {
    recues++
    effacer()
  })
  couche.on('tileerror', () => {
    erreurs++
    if (recues > 0 || erreurs < 3 || alerte) return
    alerte = document.createElement('div')
    alerte.className = 'fond-carte__alerte'
    alerte.setAttribute('role', 'status')
    alerte.textContent = `Le fond « ${nom} » ne répond pas : choisissez-en un autre.`
    carte.getContainer().append(alerte)
  })
  couche.on('remove', effacer)
}

interface EtatCarte {
  couche: L.Layer | null
  id: string | null
  /** Anciens fonds gardés sous le nouveau jusqu'à ce que celui-ci soit affiché (pas de flash blanc). */
  anciens: Set<L.Layer>
  minuteurs: Set<ReturnType<typeof setTimeout>>
}
const etats = new WeakMap<L.Map, EtatCarte>()

/** Appelle `rappel` quand la couche est affichée (ou, au plus tard, après 4 s). */
function quandAffichee(couche: L.Layer, etat: EtatCarte, rappel: () => void): void {
  const minuteur = setTimeout(rappel, 4000)
  etat.minuteurs.add(minuteur)
  const fini = () => {
    clearTimeout(minuteur)
    etat.minuteurs.delete(minuteur)
    rappel()
  }
  if (couche instanceof L.TileLayer) couche.once('load', fini)
  else (couche as L.MaplibreGL).getMaplibreMap()?.once('idle', fini)
}

/** Met `fond` sous les marqueurs de `carte`, à la place du précédent. */
function appliquerFond(carte: L.Map, fond: FondDeCarte): void {
  const etat = etats.get(carte)
  if (!etat || etat.id === fond.id) return
  // Un changement précédent encore en cours : son ancien fond n'a plus lieu d'être.
  etat.minuteurs.forEach(clearTimeout)
  etat.minuteurs.clear()
  etat.anciens.forEach((l) => carte.removeLayer(l))
  etat.anciens.clear()

  const precedent = etat.couche
  let nouvelle: L.Layer
  try {
    nouvelle = creerCouche(fond).addTo(carte)
  } catch (erreur) {
    // Un fond qui ne se construit pas ne doit jamais laisser la carte sans fond.
    console.error('Fond de carte inutilisable, retour au fond par défaut', fond.id, erreur)
    if (fond.id !== FONDS_INTEGRES[0]!.id) magasinFonds.choisir(FONDS_INTEGRES[0]!.id)
    return
  }
  if (nouvelle instanceof L.TileLayer) surveillerTuiles(carte, nouvelle, fond.nom)
  etat.couche = nouvelle
  etat.id = fond.id
  carte.getContainer().dataset.fondCarte = fond.id
  if (precedent) {
    etat.anciens.add(precedent)
    quandAffichee(nouvelle, etat, () => {
      if (etat.anciens.delete(precedent) && carte.hasLayer(precedent)) carte.removeLayer(precedent)
    })
  }
}

/**
 * Ajoute à une carte Leaflet le fond choisi par l'utilisateur (OpenFreeMap
 * « liberty » tant qu'il n'a rien choisi), son attribution et le contrôle
 * « Fond de carte ». Le fond suit ensuite les changements de choix, sur cette
 * carte comme sur toutes les autres, sans rechargement.
 */
export function ajouterFondDeCarte(carte: L.Map): void {
  // Pas de préfixe « Leaflet » : la place est comptée sur une carte de 350 px.
  carte.attributionControl.setPrefix(false)
  etats.set(carte, { couche: null, id: null, anciens: new Set(), minuteurs: new Set() })
  appliquerFond(carte, magasinFonds.actif())
  creerControleFond(magasinFonds).addTo(carte)

  const desabonner = magasinFonds.abonner(() => appliquerFond(carte, magasinFonds.actif()))
  // `map.remove()` : plus d'abonnement ni de minuteur qui traînent.
  carte.once('unload', () => {
    desabonner()
    const etat = etats.get(carte)
    etat?.minuteurs.forEach(clearTimeout)
    etats.delete(carte)
  })
}
